import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A detached panel (2026-10-05). The children keep living in THIS React tree,
 * with their state, their handlers and the page's clocks, and paint into a
 * second browser window through a portal. Closing the window, or unmounting
 * this component, brings them back.
 *
 * WHY A REAL ROUTE AND NOT about:blank. A blank window has none of the page's
 * head: no CSS bundle, no Web Awesome, no Google font, and the FontAwesome
 * kit refuses a window whose hostname is empty. So the window opens an app
 * route (`path`), which boots the SPA the way /stage does and renders ONE
 * empty mount node tagged with this instance's token; the portal lands there
 * once it exists. A stale window from an earlier lesson never matches, since
 * the token is new each time, so a fixed window name is safe (re-using it
 * brings the existing window forward instead of opening a second).
 *
 * Anthony, 2026-10-05: "I want to be able to detach the scrolling lyric
 * control column." The first user is the lyrics pane of the Lesson Dashboard
 * (songpanel/LyricsPane.js, the /lyrics-window route).
 *
 * `onClosed` fires when the PERSON closes the window; `onBlocked` when the
 * browser refused to open one (a pop-up blocker), so the caller can fall back
 * to the docked panel and say so. Neither fires on unmount.
 */
export const POPOUT_MOUNT_ATTR = 'data-popout-mount';
const POLL_MS = 150;

const makeToken = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const PopoutWindow = ({ path, name = 'cyvPopout', width = 560, height = 900, onClosed = null, onBlocked = null, children }) => {
	const [mount, setMount] = useState(null);
	const token = useRef(makeToken()).current;
	const onClosedRef = useRef(onClosed);
	onClosedRef.current = onClosed;
	const onBlockedRef = useRef(onBlocked);
	onBlockedRef.current = onBlocked;

	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.open !== 'function') return undefined;
		const url = `${path}${path.includes('?') ? '&' : '?'}k=${token}`;
		const features = `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no`;
		const win = window.open(url, name, features);
		if (!win) {
			if (onBlockedRef.current) onBlockedRef.current();
			return undefined;
		}
		if (typeof win.focus === 'function') win.focus();
		let node = null;
		const tick = setInterval(() => {
			if (win.closed) {
				clearInterval(tick);
				setMount(null);
				if (onClosedRef.current) onClosedRef.current();
				return;
			}
			if (node && node.isConnected) return;
			let found = null;
			try {
				found = win.document ? win.document.querySelector(`[${POPOUT_MOUNT_ATTR}="${token}"]`) : null;
			} catch {
				found = null;
			}
			if (found && found !== node) {
				node = found;
				setMount(found);
			}
		}, POLL_MS);
		return () => {
			clearInterval(tick);
			if (!win.closed) {
				try {
					win.close();
				} catch {
					// A window the browser will not let us close stays; its page notices the opener is gone.
				}
			}
		};
		// Opened once per mount: the token, path and name are fixed for this instance.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return mount ? createPortal(children, mount) : null;
};

export default PopoutWindow;
